import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
  type ReactNode,
} from 'react';
import { cardClasses } from './Card.js';
import { cn } from './cn.js';
import { Icon, type IconName } from './Icon.js';

/**
 * A ficha em seções — a LEITURA de um registro que foi escrito com
 * `<Form steps>`, com o mesmo desenho dele.
 *
 * Nasceu em 25/09/2026 para a ficha do sinistro. O formulário de abrir tinha
 * sete etapas com ícone, trilho e selo; a ficha do mesmo sinistro era uma lista
 * de vinte linhas "rótulo · valor" num cartão só, e quem abria não achava nada
 * sem ler linha por linha. As duas telas contam a mesma coisa, então passaram
 * a ter a mesma forma: a seção que se preencheu com o ícone do carro é a que se
 * lê com o ícone do carro.
 *
 * - **O trilho** sai das próprias seções, como o do `Form`: ícone e nome, com a
 *   seção que está na tela acesa. Não há número nem selo — na leitura não há
 *   ordem a cumprir nem nada faltando. Abaixo de `lg` ele some: a ficha já
 *   abre com o resumo, e uma faixa fixa a mais comeria a tela do telefone.
 * - **A seção** repete o cabeçalho de etapa do `Form` — o quadro de 40px com o
 *   ícone, o título em 14px peso 700 —, e o canto direito é de quem monta:
 *   uma contagem, um selo, uma ação.
 *
 * Mora aqui, e não no módulo, pelo mesmo motivo do `Form`: o quadro do ícone é
 * na cor da marca, e essa cor só é desenhada dentro deste pacote (régua
 * `primaria-solta`). Escrito no módulo, ele sairia cinza, e a ficha deixaria de
 * ser gêmea do formulário.
 *
 * A seção se REGISTRA no trilho ao montar, e sai dele ao desmontar — a seção
 * condicional (os terceiros, só no do associado) entra e sai sozinha.
 */
export function DetailSections({
  children,
  label = 'Seções da ficha',
}: {
  children: ReactNode;
  /** O nome do trilho para leitor de tela. */
  label?: string;
}) {
  const [items, setItems] = useState<readonly RailItem[]>([]);

  const register = useCallback((item: RailItem) => {
    setItems((current) => sortByDocument([...current.filter((i) => i.id !== item.id), item]));
    return () => setItems((current) => current.filter((i) => i.id !== item.id));
  }, []);

  return (
    <Registry.Provider value={register}>
      <div
        className={cn(
          'grid items-start gap-5',
          items.length > 1 && 'lg:grid-cols-[13.5rem_minmax(0,1fr)]',
        )}
      >
        {items.length > 1 && <DetailRail items={items} label={label} />}
        <div className="flex min-w-0 flex-col gap-5">{children}</div>
      </div>
    </Registry.Provider>
  );
}

/**
 * Como uma seção se anuncia ao trilho. Por registro, e não pelo tipo do filho
 * como o `Form` faz: a ficha monta cada seção num componente próprio
 * (`VehicleSection`, `TowSection`), e o filho direto é o embrulho.
 */
const Registry = createContext<((item: RailItem) => () => void) | null>(null);

/** A ordem do trilho é a da página, e não a de quem se registrou primeiro. */
function sortByDocument(items: RailItem[]): RailItem[] {
  return items.sort((a, b) => {
    const ea = document.getElementById(a.id);
    const eb = document.getElementById(b.id);
    if (!ea || !eb) return 0;
    return ea.compareDocumentPosition(eb) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });
}

export interface DetailSectionProps {
  /** O assunto — o MESMO título da seção do formulário que o escreveu. */
  title: ReactNode;
  /** O ícone do assunto — o mesmo da seção do formulário. */
  icon: IconName;
  /** Uma frase sobre de onde o dado vem, quando isso não é óbvio. */
  descricao?: ReactNode;
  /** O canto direito do cabeçalho: uma contagem, um selo, uma ação. */
  actions?: ReactNode;
  /** Corpo sem recuo, para tabela que encosta na borda. */
  bleed?: boolean;
  children?: ReactNode;
  className?: string;
}

/** Um assunto da ficha, com o cabeçalho de etapa do `Form steps`. */
export function DetailSection({
  title,
  icon,
  descricao,
  actions,
  bleed = false,
  children,
  className,
}: DetailSectionProps) {
  const id = useId();
  const register = useContext(Registry);
  /* O título do trilho é o texto do título; um nó rico não cabe na linha dele. */
  const railTitle = typeof title === 'string' ? title : '';
  useEffect(() => register?.({ id, title: railTitle, icon }), [register, id, railTitle, icon]);
  return (
    <section
      id={id}
      className={cardClasses({
        padding: false,
        className: cn('scroll-mt-4', className),
      })}
    >
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-borda-suave px-4 py-3.5">
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-borda bg-primaria-suave text-primaria-texto"
        >
          <Icon nome={icon} size={20} />
        </span>
        <h2 className="min-w-40 flex-1 text-sm font-bold text-texto">{title}</h2>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </header>

      {descricao && <p className="px-4 pt-3 text-xs text-texto-suave">{descricao}</p>}

      <div className={cn(bleed ? 'pt-1' : 'px-4 pb-4', !bleed && (descricao ? 'pt-3' : 'pt-4'))}>
        {children}
      </div>
    </section>
  );
}

/**
 * Os dados de uma seção, em grade — dois por linha a partir do tablet, três
 * na tela larga. Um dado comprido (endereço, descrição) pede `wide`.
 */
export function Facts({ children }: { children: ReactNode }) {
  return (
    <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">{children}</dl>
  );
}

/**
 * Um dado: o rótulo pequeno em cima, o valor embaixo, no peso de quem é lido.
 *
 * O rótulo é o do CAMPO do formulário, sem a pergunta ("Câmeras no local", e
 * não "O local tem câmeras?"): na leitura já não há ninguém a quem perguntar.
 *
 * O `icon` (28/10/2026) vai na linha do rótulo, antes dele — `<ícone> Rótulo`
 * em cima, o dado embaixo —, e é decorativo: quem diz o que o dado é continua
 * sendo o rótulo.
 */
export function Fact({
  label,
  children,
  wide = false,
  icon,
}: {
  label: ReactNode;
  children: ReactNode;
  /** Ocupa a linha inteira da grade. */
  wide?: boolean;
  icon?: IconName;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', wide && 'sm:col-span-full')}>
      <dt className="flex items-center gap-1.5 text-xs font-medium text-texto-suave">
        {icon && <Icon nome={icon} size={16} className="shrink-0 text-texto-tenue" />}
        {label}
      </dt>
      <dd className="min-w-0 text-sm font-semibold wrap-anywhere text-texto">{children}</dd>
    </div>
  );
}

interface RailItem {
  id: string;
  title: string;
  icon: IconName;
}

/**
 * O trilho da ficha. O mesmo lugar e a mesma largura do trilho do `Form`
 * (13,5rem, fixo no topo de quem rola), e a mesma conta de "na tela": quem
 * cruza a faixa entre 20% e 30% da altura da janela.
 *
 * Os itens são BOTÕES, e não âncoras, pelo motivo do `Form`: um `href="#…"`
 * passaria pelo roteador e escreveria o hash na URL.
 */
function DetailRail({ items, label }: { items: readonly RailItem[]; label: string }) {
  const [current, setCurrent] = useState(0);
  const ids = items.map((i) => i.id).join(' ');

  useEffect(() => {
    const sections = ids
      .split(' ')
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setCurrent(sections.indexOf(entry.target as HTMLElement));
        }
      },
      { rootMargin: '-20% 0px -70% 0px' },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [ids]);

  return (
    <nav
      aria-label={label}
      className="sticky top-0 hidden rounded-cartao border border-borda bg-superficie p-2 lg:block"
    >
      <ol className="flex flex-col gap-0.5">
        {items.map((item, index) => {
          const isCurrent = index === current;
          return (
            <li key={item.id}>
              <button
                type="button"
                aria-current={isCurrent ? 'location' : undefined}
                onClick={() =>
                  document
                    .getElementById(item.id)
                    ?.scrollIntoView({ block: 'start', behavior: 'smooth' })
                }
                className={cn(
                  'flex min-h-11 w-full items-center gap-2.5 rounded-borda px-2 py-1.5 text-left transition-colors',
                  'hover:bg-superficie-suave active:bg-borda',
                  isCurrent && 'bg-primaria-suave hover:bg-primaria-suave',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-pilula border-[1.5px]',
                    isCurrent
                      ? 'border-primaria-texto bg-primaria text-primaria-contraste'
                      : 'border-borda-forte bg-superficie text-texto-suave',
                  )}
                >
                  <Icon nome={item.icon} size={16} />
                </span>
                <span className="min-w-0 text-xs font-semibold leading-tight text-texto">
                  {item.title}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
